import { useMemo } from 'react';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import ExportButton from '@/components/Chart/ExportButton';
import type { ChartType } from '@/components/Chart/types/highcharts';
import type { SymbolKey } from '@/entities/symbol';
import { overrideSymbols } from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch } from '@/hooks/redux-hooks';
import useUserDetails from '@/hooks/useUserDetails';
import { useOptionalMarketWidget } from '@/pages/Markets/MarketsComponents/contexts/MarketWidgetContext';
import { useLightMode } from '@/pages/Markets/MarketsComponents/LightMode/contexts/LightModeContext';
import { openExportDrawer } from '@/slices/drawerSlice';
import { setExport } from '@/slices/exportSlice';

const ExportChartButton = ({
  disabled = false,
  graphType,
  metadata
}: {
  graphType: ChartType | 'fullscreen';
  disabled?: boolean;
  metadata?: Record<string, string>;
}) => {
  const { isUserExpired } = useUserDetails();
  const { chartSeries, chartRef } = useChartContext();
  const { isLightMode } = useLightMode();
  const { isFullScreen } = useOptionalMarketWidget();
  const dispatch = useAppDispatch();
  const type = useMemo(() => {
    const temp = graphType || 'spot';
    return temp === 'spot' && isFullScreen ? 'fullscreen' : temp;
  }, [graphType, isFullScreen]);

  const exportSelection = () => {
    if (chartSeries) {
      let selectedTimeRange: string | undefined = undefined;
      const selectedSymbols: SymbolKey[] = chartSeries.map(series => {
        const custom = series.custom;
        return {
          Code: custom.Code,
          Period: custom.Period || '',
          PeriodType: custom.PeriodType,
          TimeRef: custom.TimeRef,
          transactionDate: custom.transactionDate
        };
      });
      if (chartRef.current?.chart) {
        const chart = chartRef.current?.chart;
        const selectedTimeRangeButtonIndex = chart.rangeSelector?.selected;
        if (selectedTimeRangeButtonIndex !== undefined) {
          const buttonConfig = chart.rangeSelector.buttonOptions[selectedTimeRangeButtonIndex];
          if (buttonConfig) {
            selectedTimeRange = buttonConfig.text === 'Max' ? selectedTimeRange : buttonConfig.text;
          }
        }
      }
      dispatch(overrideSymbols({ symbols: selectedSymbols }));
      dispatch(openExportDrawer({ showHistory: false }));
      dispatch(
        setExport({ exportType: type, from: metadata?.from, isLightMode, selectedTimeRange })
      );
    }
  };

  const isDisabled = disabled || isUserExpired;

  return (
    <ExportButton
      disabled={isDisabled}
      handleClick={exportSelection}
      title='Export chart'
    />
  );
};

export default ExportChartButton;
